import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { ApiRequestError, confirmReserve, fetchReserveOptions, reserve, reserveHold } from '../lib/api'
import { uiStrings } from '../lib/strings'
import { track } from '../lib/telemetry'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * `/reserve` — the way into booking, from the boutique's website or from a
 * fitting room's last page (`?s=<its token>`). `docs/23`.
 *
 * Who they are and which appointment, the no-show policy and a box to agree
 * to it; then Stripe's own page saves the card (nothing about the card is
 * typed here), and Koalendar's calendar takes the time. Without Stripe set
 * up, or with no fee, it goes straight to the calendar.
 */
export function ReserveRoute() {
  const t = uiStrings(navigator.language || 'en')
  const [params] = useSearchParams()
  const sessionToken = params.get('s') ?? undefined
  const options = useQuery({
    queryKey: ['public', 'reserve', sessionToken ?? null] as const,
    queryFn: ({ signal }) => fetchReserveOptions(sessionToken, signal),
    retry: false,
  })
  const data = options.data

  const [tierKey, setTierKey] = useState(params.get('tier') ?? '')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  // What a fitting room already knows, once, when the options arrive.
  const filled = useRef(false)
  useEffect(() => {
    if (!data || filled.current) return
    filled.current = true
    const known = data.prefill
    if (known?.name) setName(known.name)
    if (known?.email) setEmail(known.email)
    if (known?.phone) setPhone(known.phone)
    const offered = data.tiers.map((tier) => tier.key)
    const wanted = [params.get('tier'), known?.tierKey].find((key) => key && offered.includes(key))
    setTierKey(wanted ?? (offered.length === 1 ? (offered[0] ?? '') : ''))
  }, [data, params])

  // Each type says for itself whether it saves a card first.
  const takesCard = data?.tiers.find((tier) => tier.key === tierKey)?.takesCard ?? false

  const go = useMutation({
    mutationFn: reserve,
    onSuccess: (next) => window.location.assign(next.url),
  })

  const problems = useMemo(() => {
    const out: Partial<Record<'tier' | 'name' | 'email' | 'agree', true>> = {}
    if (!tierKey) out.tier = true
    if (!name.trim()) out.name = true
    if (!EMAIL.test(email.trim())) out.email = true
    if (takesCard && !agreed) out.agree = true
    return out
  }, [tierKey, name, email, agreed, takesCard])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setSubmitted(true)
    if (Object.keys(problems).length > 0) return
    track('reserve_start', { tier: tierKey, card: takesCard, from_room: Boolean(sessionToken) })
    go.mutate({
      tierKey,
      name: name.trim(),
      email: email.trim(),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(sessionToken ? { sessionToken } : {}),
      agreed,
    })
  }

  return (
    <Page>
      {options.isPending ? (
        <p style={{ color: 'var(--fr-ink-dim)' }}>{t.reserveWorking}</p>
      ) : !data || data.tiers.length === 0 ? (
        <>
          <h1 className="fr-display text-[2rem] leading-[1.08]">{t.reserveTitle}</h1>
          <p className="mt-3 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
            {t.reserveClosed}
          </p>
        </>
      ) : data.alreadyBooked && sessionToken ? (
        <>
          <h1 className="fr-display text-[2rem] leading-[1.08]">{t.reserveAlreadyBooked}</h1>
          <a className="fr-btn fr-btn-primary mt-7 block w-full text-center" href={`/s/${encodeURIComponent(sessionToken)}`}>
            {t.resumeFitting}
          </a>
        </>
      ) : (
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          <div>
            <p className="fr-eyebrow">{data.boutiqueName}</p>
            <h1 className="fr-display mt-2 text-[2rem] leading-[1.08]">{t.reserveTitle}</h1>
            <p className="mt-3 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
              {t.reserveBody}
            </p>
          </div>

          {data.tiers.length > 1 ? (
            <fieldset className="flex flex-col gap-2">
              <legend className="fr-eyebrow mb-1">{t.reserveType}</legend>
              {data.tiers.map((tier) => (
                <label key={tier.key} className="fr-pick-option flex items-center gap-3 border px-4 py-3" style={{ borderColor: tierKey === tier.key ? 'var(--fr-primary)' : 'var(--fr-rule)' }}>
                  <input type="radio" name="tier" value={tier.key} checked={tierKey === tier.key} onChange={() => setTierKey(tier.key)} />
                  <span className="flex-1">{tier.label}</span>
                  {tier.durationMinutes ? <span className="text-[0.8125rem]" style={{ color: 'var(--fr-ink-faint)' }}>{t.appointmentLength(tier.durationMinutes)}</span> : null}
                </label>
              ))}
              {submitted && problems.tier ? <Problem>{t.reserveChooseType}</Problem> : null}
            </fieldset>
          ) : (
            <p className="fr-eyebrow">{data.tiers[0]?.label}</p>
          )}

          <Field label={t.reserveName} problem={submitted && problems.name}>
            <input className="fr-input" autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label={t.reserveEmail} problem={submitted && problems.email}>
            <input className="fr-input" type="email" inputMode="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          </Field>
          <Field label={t.reservePhone}>
            <input className="fr-input" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(event) => setPhone(event.target.value)} />
          </Field>

          {takesCard && data.policy ? (
            <section className="border p-4" style={{ borderColor: 'var(--fr-rule)', background: 'var(--fr-surface)' }}>
              <p className="fr-eyebrow mb-2">{t.reservePolicyTitle}</p>
              <p className="text-[0.9375rem] leading-relaxed" data-testid="reserve-policy">
                {data.policy}
              </p>
              <label className="mt-3 flex items-start gap-2 text-[0.9375rem]">
                <input type="checkbox" className="mt-1" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
                <span>{t.reserveAgree}</span>
              </label>
              {submitted && problems.agree ? <Problem>{t.reserveAgreeNeeded}</Problem> : null}
              <p className="mt-3 text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-faint)' }}>
                {t.reserveCardNote}
              </p>
            </section>
          ) : null}

          {go.isError ? <Problem>{go.error instanceof ApiRequestError ? go.error.message : t.reserveFailed}</Problem> : null}
          <button type="submit" className="fr-btn fr-btn-primary w-full" disabled={go.isPending || go.isSuccess}>
            {go.isPending || go.isSuccess ? t.reserveWorking : takesCard ? t.reserveToCard : t.reserveToCalendar}
          </button>
        </form>
      )}
    </Page>
  )
}

/**
 * `/hold?tier=<type>` — where the website's link (`/go/<type>`) lands for a
 * type that saves a card first. The policy and a box to tick, nothing else:
 * Stripe's page takes the card and the name on it, and Koalendar's asks who
 * is coming. Then `/reserve/done`, then the calendar.
 */
export function HoldRoute() {
  const t = uiStrings(navigator.language || 'en')
  const [params] = useSearchParams()
  const tierKey = params.get('tier') ?? ''
  const options = useQuery({
    queryKey: ['public', 'reserve', null] as const,
    queryFn: ({ signal }) => fetchReserveOptions(undefined, signal),
    retry: false,
  })
  const tier = options.data?.tiers.find((entry) => entry.key === tierKey)
  const [agreed, setAgreed] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const go = useMutation({
    mutationFn: () => reserveHold({ tierKey, agreed: true }),
    onSuccess: (next) => window.location.assign(next.url),
  })
  const submit = (event: FormEvent) => {
    event.preventDefault()
    setSubmitted(true)
    if (!agreed) return
    track('hold_start', { tier: tierKey })
    go.mutate()
  }

  return (
    <Page>
      {options.isPending ? (
        <p style={{ color: 'var(--fr-ink-dim)' }}>{t.reserveWorking}</p>
      ) : !options.data || !tier ? (
        <>
          <h1 className="fr-display text-[2rem] leading-[1.08]">{t.reserveTitle}</h1>
          <p className="mt-3 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
            {t.reserveClosed}
          </p>
        </>
      ) : !tier.takesCard || !options.data.policy ? (
        // Nothing to hold with: the website's link goes straight to the calendar.
        <>
          <h1 className="fr-display text-[2rem] leading-[1.08]">{t.reserveTitle}</h1>
          <a className="fr-btn fr-btn-primary mt-7 block w-full text-center" href={`/go/${encodeURIComponent(tier.key)}`}>
            {t.reserveToCalendar}
          </a>
        </>
      ) : (
        <form onSubmit={submit} noValidate className="flex flex-col gap-4" data-testid="hold-form">
          <div>
            <p className="fr-eyebrow">{options.data.boutiqueName}</p>
            <h1 className="fr-display mt-2 text-[2rem] leading-[1.08]">{t.holdTitle}</h1>
            <p className="mt-2 text-[1rem]">
              {tier.label}
              {tier.durationMinutes ? <span style={{ color: 'var(--fr-ink-faint)' }}> · {t.appointmentLength(tier.durationMinutes)}</span> : null}
            </p>
            <p className="mt-3 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
              {t.holdBody}
            </p>
          </div>
          <section className="border p-4" style={{ borderColor: 'var(--fr-rule)', background: 'var(--fr-surface)' }}>
            <p className="fr-eyebrow mb-2">{t.reservePolicyTitle}</p>
            <p className="text-[0.9375rem] leading-relaxed" data-testid="hold-policy">
              {options.data.policy}
            </p>
            <label className="mt-3 flex items-start gap-2 text-[0.9375rem]">
              <input type="checkbox" className="mt-1" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
              <span>{t.reserveAgree}</span>
            </label>
            {submitted && !agreed ? <Problem>{t.reserveAgreeNeeded}</Problem> : null}
            <p className="mt-3 text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-faint)' }}>
              {t.reserveCardNote}
            </p>
          </section>
          {go.isError ? <Problem>{go.error instanceof ApiRequestError ? go.error.message : t.reserveFailed}</Problem> : null}
          <button type="submit" className="fr-btn fr-btn-primary w-full" disabled={go.isPending || go.isSuccess}>
            {go.isPending || go.isSuccess ? t.reserveWorking : t.reserveToCard}
          </button>
        </form>
      )}
    </Page>
  )
}

/**
 * `/reserve/done?r=<reservation>` — back from Stripe. The API asks Stripe
 * whether the card was saved; then on to Koalendar's calendar.
 */
export function ReserveDoneRoute() {
  const t = uiStrings(navigator.language || 'en')
  const [params] = useSearchParams()
  const intentId = params.get('r') ?? ''
  const confirm = useQuery({
    queryKey: ['public', 'reserve-confirm', intentId] as const,
    queryFn: () => confirmReserve(intentId),
    enabled: intentId !== '',
    retry: false,
    staleTime: Infinity,
  })
  const url = confirm.data?.url
  useEffect(() => {
    if (!url) return
    track('card_saved')
    // A moment to read that the card is on file, then the calendar.
    const timer = setTimeout(() => window.location.assign(url), 1200)
    return () => clearTimeout(timer)
  }, [url])

  return (
    <Page>
      {confirm.data ? (
        <>
          <h1 className="fr-display text-[2rem] leading-[1.08]">{t.reserveTitle}</h1>
          <p className="mt-3 text-[0.9375rem] leading-relaxed" data-testid="reserve-card-saved">
            {t.reserveCardSaved(confirm.data.cardBrand, confirm.data.cardLast4)}
          </p>
          <a className="fr-btn fr-btn-primary mt-7 block w-full text-center" href={confirm.data.url}>
            {t.reserveToCalendar}
          </a>
        </>
      ) : confirm.isError || !intentId ? (
        <>
          <h1 className="fr-display text-[2rem] leading-[1.08]">{t.reserveCardNotSaved}</h1>
          {confirm.error instanceof ApiRequestError ? (
            <p className="mt-3 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
              {confirm.error.message}
            </p>
          ) : null}
          <a className="fr-btn fr-btn-primary mt-7 block w-full text-center" href="/reserve">
            {t.reserveBackToStart}
          </a>
        </>
      ) : (
        <p style={{ color: 'var(--fr-ink-dim)' }}>{t.reserveWorking}</p>
      )}
    </Page>
  )
}

/** The frame the standalone customer pages share: the home page's, narrow and centered. */
export function Page({ children }: { readonly children: React.ReactNode }) {
  return (
    <div className="fr-room min-h-screen">
      <div className="mx-auto flex min-h-screen w-full max-w-[520px] flex-col justify-center px-5 py-12">{children}</div>
    </div>
  )
}

function Field({ label, problem, children }: { readonly label: string; readonly problem?: boolean | undefined; readonly children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="fr-eyebrow" style={problem ? { color: 'var(--fr-danger, #9f1239)' } : undefined}>
        {label}
      </span>
      {children}
    </label>
  )
}

function Problem({ children }: { readonly children: React.ReactNode }) {
  return (
    <p role="alert" className="text-[0.875rem]" style={{ color: 'var(--fr-danger, #9f1239)' }}>
      {children}
    </p>
  )
}
